/*
  Puff's Collects — forgiving storefront search.

  Shopify's native /search is a whole-word index: "tai" finds nothing because
  "tai" is not a word in "Taiyaki Set", and no query syntax makes it tolerate a
  misspelling like "taiaki". The catalog is small enough to search in the
  browser instead, so this pulls a compact index once (templates/
  collection.search-index.liquid) and matches locally with prefix, substring and
  edit-distance scoring.

  Used by the header search dialog, the mobile drawer search, and as the
  zero-result fallback on the /search results page.
*/

(() => {
  const INDEX_CACHE_KEY = 'puffs:search-index:v1';
  const MAX_RESULTS = 6;
  const MAX_FALLBACK_RESULTS = 12;

  /*
    Match weights. Every layer below scores one query term against one product;
    the best layer wins, terms are averaged, and products sort by the result.
    Tune here rather than in the scoring functions.
  */
  const WEIGHTS = {
    titleExact: 120,        // "taiyaki set" typed in full
    titleWordExact: 110,    // "taiyaki" — a whole word of the title
    titlePrefix: 100,       // "taiy" — title starts with it
    titleWordPrefix: 90,    // "tai" — a title word starts with it  <- the reported bug
    titleSubstring: 70,     // "aiyak" — appears mid-word
    keywordMatch: 55,       // matches a tag, product type, vendor or collection
    titleFuzzy: 60,         // "taiaki" — typo against a title word
    keywordFuzzy: 35,       // typo against a tag/type/collection word
    perEditPenalty: 15,     // subtracted from the fuzzy score for each typo
    partialQueryFactor: 0.35, // multi-word queries where only some words matched
    unavailablePenalty: 8,  // sold-out items still show, just lower
  };

  /* Typo budget by term length. Short terms stay strict: at 3 characters almost
     everything is 1 edit away from everything else, which is pure noise. */
  const editBudget = (term) => (term.length <= 3 ? 0 : term.length <= 5 ? 1 : 2);

  const normalize = (value) =>
    (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const tokenize = (value) => normalize(value).split(' ').filter(Boolean);

  /*
    Optimal string alignment distance (Levenshtein plus adjacent transposition,
    so "taiyaki" -> "tayiaki" costs 1 rather than 2). Bails out as soon as every
    cell in a row exceeds `max`, which keeps the common non-match case cheap.
  */
  function editDistance(a, b, max) {
    const al = a.length;
    const bl = b.length;
    if (Math.abs(al - bl) > max) return max + 1;
    if (!al) return bl;
    if (!bl) return al;

    let beforePrev = [];
    let prev = [];
    for (let j = 0; j <= bl; j += 1) prev[j] = j;

    for (let i = 1; i <= al; i += 1) {
      const row = [i];
      let bestInRow = i;
      for (let j = 1; j <= bl; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let value = Math.min(row[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          value = Math.min(value, beforePrev[j - 2] + 1);
        }
        row[j] = value;
        if (value < bestInRow) bestInRow = value;
      }
      if (bestInRow > max) return max + 1;
      beforePrev = prev;
      prev = row;
    }
    return prev[bl];
  }

  /* Best fuzzy hit for one term across a word list. Tries the whole word first
     ("taiaki" vs "taiyaki"), then the word's leading slice, so a typo inside a
     partial term still lands ("tayak" vs the "taiyak" of "taiyaki"). */
  function fuzzyBest(term, words, base) {
    const budget = editBudget(term);
    if (!budget) return null;
    let best = null;
    for (const word of words) {
      let distance = editDistance(term, word, budget);
      if (distance > budget && word.length > term.length) {
        distance = editDistance(term, word.slice(0, term.length), budget);
      }
      if (distance > budget) continue;
      const score = base - distance * WEIGHTS.perEditPenalty;
      if (score > 0 && (!best || score > best.score)) best = { score, word };
    }
    return best;
  }

  function scoreTerm(term, entry) {
    if (entry.title === term) return { score: WEIGHTS.titleExact };
    // Whole-word before prefix: "jumbo" is a complete word of "Jumbo Mochi Set",
    // and testing startsWith first would score it as a mere prefix.
    for (const word of entry.titleWords) {
      if (word === term) return { score: WEIGHTS.titleWordExact };
    }
    if (entry.title.startsWith(term)) return { score: WEIGHTS.titlePrefix };

    for (const word of entry.titleWords) {
      if (word.startsWith(term)) return { score: WEIGHTS.titleWordPrefix };
    }
    if (entry.title.includes(term)) return { score: WEIGHTS.titleSubstring };
    for (const word of entry.keywordWords) {
      if (word.startsWith(term)) return { score: WEIGHTS.keywordMatch };
    }

    const titleFuzzy = fuzzyBest(term, entry.titleWords, WEIGHTS.titleFuzzy);
    if (titleFuzzy) return { score: titleFuzzy.score, correction: titleFuzzy.word };

    const keywordFuzzy = fuzzyBest(term, entry.keywordWords, WEIGHTS.keywordFuzzy);
    if (keywordFuzzy) return { score: keywordFuzzy.score, correction: keywordFuzzy.word };

    return { score: 0 };
  }

  function scoreEntry(terms, entry) {
    let total = 0;
    let matched = 0;
    const corrections = [];

    for (const term of terms) {
      const result = scoreTerm(term, entry);
      if (result.score <= 0) {
        corrections.push(term);
        continue;
      }
      total += result.score;
      matched += 1;
      corrections.push(result.correction || term);
    }

    if (!matched) return null;
    let score = total / terms.length;
    if (matched < terms.length) score *= WEIGHTS.partialQueryFactor;
    if (!entry.available) score -= WEIGHTS.unavailablePenalty;
    return { score, corrected: corrections.join(' ') };
  }

  function search(query, entries, limit) {
    const terms = tokenize(query);
    if (!terms.length) return { hits: [], corrected: null };

    const scored = [];
    for (const entry of entries) {
      const result = scoreEntry(terms, entry);
      if (result) scored.push({ entry, ...result });
    }
    scored.sort((a, b) => b.score - a.score || a.entry.title.length - b.entry.title.length);

    const hits = scored.slice(0, limit);
    // Only offer a spelling suggestion when nothing matched cleanly.
    const best = hits[0];
    const corrected =
      best && best.score < WEIGHTS.keywordMatch && best.corrected !== terms.join(' ')
        ? best.corrected
        : null;
    return { hits, corrected };
  }

  /* ---------------------------------------------------------------- index */

  /* Mirrors snippets/puffs-display-title.liquid so suggestions read the same as
     the product cards. Keep the two in step. */
  const DISPLAY_TITLES = {
    'pastry-set': 'Pastry Set',
    'sliced-fruit-set': 'Sliced Fruit Set',
    'taiyaki-set': 'Taiyaki Set',
    soap: 'Soap Set',
    'square-bread': 'Square Bread Set',
  };

  /* `preorder` and `component` sit on nearly every product, so as search
     keywords they only add noise. `parent:taiyaki-set` keeps its value — that is
     what lets "taiyaki" surface the individual taiyaki squishies. */
  const IGNORED_TAGS = new Set(['preorder', 'component']);

  const compact = (product) => {
    const handle = product.handle;
    const tags = (product.tags || [])
      .filter((tag) => !IGNORED_TAGS.has(tag))
      .map((tag) => tag.replace(/^parent:/, ''));
    const image = product.images && product.images[0] ? product.images[0].src : null;
    return {
      t: DISPLAY_TITLES[handle] || product.title,
      u: `/products/${handle}`,
      i: image ? `${image}${image.includes('?') ? '&' : '?'}width=160` : null,
      y: product.product_type || '',
      a: (product.variants || []).some((variant) => variant.available),
      k: [product.product_type, product.vendor, ...tags].filter(Boolean).join(' '),
    };
  };

  const prepare = (product) => ({
    title: normalize(product.t),
    titleWords: tokenize(product.t),
    keywordWords: [...new Set(tokenize(product.k))],
    available: product.a !== false,
    label: product.t,
    url: product.u,
    image: product.i,
    type: product.y || '',
  });

  let indexPromise = null;

  function readCache() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(INDEX_CACHE_KEY));
      return Array.isArray(cached?.products) ? cached.products : null;
    } catch {
      return null;
    }
  }

  function writeCache(products) {
    try {
      sessionStorage.setItem(INDEX_CACHE_KEY, JSON.stringify({ products }));
    } catch {
      /* private mode or quota — the index just refetches next page load */
    }
  }

  /*
    Built from Shopify's own /products.json feed rather than a theme template.
    An alternate collection template (?view=...) was the tidier source, but this
    store does not serve newly added alternate templates — only ones that
    predate the current theme — so the feed is the dependable route. It also
    needs no theme file at all, and only published products come back.

    Prices are deliberately not indexed: the feed reports shop currency (CAD)
    and ignores Shopify Markets, so a USD shopper would be shown a CAD number.
    Suggestions show the product type instead.
  */
  const PAGE_SIZE = 250;
  const MAX_INDEX_PAGES = 8; // backstop; 2000 products is far beyond this catalog

  async function fetchProductPage(page) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}products.json?limit=${PAGE_SIZE}&page=${page}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Product feed page ${page} failed: ${response.status}`);
    const payload = await response.json();
    return payload.products || [];
  }

  async function fetchAllProducts() {
    const products = [];
    for (let page = 1; page <= MAX_INDEX_PAGES; page += 1) {
      const batch = await fetchProductPage(page);
      products.push(...batch);
      if (batch.length < PAGE_SIZE) break;
    }
    return products.map(compact);
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    indexPromise = (async () => {
      const cached = readCache();
      if (cached) return cached.map(prepare);
      const products = await fetchAllProducts();
      writeCache(products);
      return products.map(prepare);
    })().catch((error) => {
      indexPromise = null; // let the next keystroke retry
      throw error;
    });
    return indexPromise;
  }

  /* ------------------------------------------------------------------- ui */

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

  function hitMarkup(hit, index) {
    const { entry } = hit;
    const image = entry.image
      ? `<img class="puffs-search__hit-image" src="${escapeHtml(entry.image)}" alt="" loading="lazy" width="56" height="56">`
      : '<span class="puffs-search__hit-image puffs-search__hit-image--empty" aria-hidden="true"></span>';
    const meta = [entry.type, entry.available ? '' : 'Sold out'].filter(Boolean).join(' · ');
    return `
      <a class="puffs-search__hit" role="option" id="PuffsSearchHit${index}" aria-selected="false" href="${escapeHtml(entry.url)}" tabindex="-1">
        ${image}
        <span class="puffs-search__hit-body">
          <span class="puffs-search__hit-title">${escapeHtml(entry.label)}</span>
          <span class="puffs-search__hit-meta">${escapeHtml(meta)}</span>
        </span>
      </a>`;
  }

  function resultsMarkup({ hits, corrected }, query) {
    if (!hits.length) {
      return `<p class="puffs-search__empty">No squishies match “${escapeHtml(query)}”. Try a shorter word.</p>`;
    }
    const note = corrected
      ? `<p class="puffs-search__note">Showing closest matches for <strong>${escapeHtml(corrected)}</strong></p>`
      : '';
    return `${note}<div class="puffs-search__hits">${hits.map(hitMarkup).join('')}</div>`;
  }

  const announce = (message) => {
    const region = document.getElementById('PuffsLiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  };

  class PuffsSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-search-results]');
      if (!this.input || !this.panel) return;

      this.entries = null;
      this.activeIndex = -1;
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.panel.id);

      this.input.addEventListener('input', () => this.render(), { signal });
      this.input.addEventListener('focus', () => this.warm(), { signal });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.addEventListener('pointerdown', () => this.warm(), { signal });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      }, { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.entries = null;
    }

    async warm() {
      if (this.entries) return this.entries;
      try {
        this.entries = await loadIndex();
      } catch {
        this.entries = null;
      }
      return this.entries;
    }

    async render() {
      const query = this.input.value.trim();
      if (query.length < 2) return this.close();

      const entries = await this.warm();
      if (!entries || this.input.value.trim() !== query) return;

      const results = search(query, entries, MAX_RESULTS);
      this.hits = results.hits;
      this.activeIndex = -1;
      this.panel.innerHTML = resultsMarkup(results, query);
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      announce(
        results.hits.length
          ? `${results.hits.length} suggestion${results.hits.length === 1 ? '' : 's'} for ${query}`
          : `No suggestions for ${query}`
      );
    }

    close() {
      this.panel.hidden = true;
      this.panel.innerHTML = '';
      this.hits = [];
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    onKeydown(event) {
      const options = this.panel.hidden ? [] : [...this.panel.querySelectorAll('.puffs-search__hit')];

      if (event.key === 'Escape') {
        if (this.panel.hidden) return; // let the dialog handle its own close
        event.stopPropagation();
        this.close();
        return;
      }
      if (!options.length) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + step + options.length + 1) % (options.length + 1);
        this.highlight(options);
        return;
      }
      if (event.key === 'Enter' && this.activeIndex >= 0 && options[this.activeIndex]) {
        event.preventDefault();
        options[this.activeIndex].click();
      }
    }

    highlight(options) {
      options.forEach((option, index) => {
        const active = index === this.activeIndex;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      const active = options[this.activeIndex];
      if (active) {
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }
  }

  customElements.define('puffs-search', PuffsSearch);

  // Exposed for console debugging and the test harness in tests/.
  window.PuffsSearch = { search, prepare, compact, tokenize, editDistance, loadIndex, WEIGHTS };

  /*
    /search?q=tai returns nothing from Shopify's own index, so the results page
    fills in from the local one rather than dead-ending on "No squishies found".
  */
  async function renderResultsFallback() {
    const container = document.querySelector('[data-puffs-search-fallback]');
    if (!container) return;
    const query = (container.dataset.query || '').trim();
    if (!query) return;

    let entries;
    try {
      entries = await loadIndex();
    } catch {
      return;
    }

    const results = search(query, entries, MAX_FALLBACK_RESULTS);
    if (!results.hits.length) return;

    const note = results.corrected
      ? `No exact match for <strong>${escapeHtml(query)}</strong> — showing the closest matches for <strong>${escapeHtml(results.corrected)}</strong>.`
      : `No exact match for <strong>${escapeHtml(query)}</strong> — here is the closest we have.`;
    container.innerHTML = `
      <p class="puffs-search__fallback-note">${note}</p>
      <div class="puffs-search__hits puffs-search__hits--grid">${results.hits.map(hitMarkup).join('')}</div>`;
    container.hidden = false;

    /* Shopify already printed "0 results / No squishies found" server-side.
       Now that there is something to show, that copy would contradict it. */
    document.querySelectorAll('[data-puffs-native-empty]').forEach((node) => {
      node.hidden = true;
    });
    announce(`${results.hits.length} close matches for ${query}`);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderResultsFallback, { once: true });
  } else {
    renderResultsFallback();
  }
})();
